import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Box, IconButton, Tooltip, Typography, Button } from '@mui/material';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import useThemeStyles from '../../../../hooks/useThemeStyles';

const RightSidebar = ({ modes = [], defaultMode = null, onToggle, ...otherProps }) => {
  const { colors } = useThemeStyles();
  const [selectedMode, setSelectedMode] = useState(
    defaultMode || (modes.length > 0 ? modes[0].id : null)
  );
  const [isExpanded, setIsExpanded] = useState(true);

  // If no modes provided, return null or a default view
  if (!modes || modes.length === 0) {
    return null;
  }

  const currentMode = modes.find((mode) => mode.id === selectedMode) || modes[0];
  const ModeBodyComponent = currentMode?.barBodyComponent;

  const handleModeChange = (modeId) => {
    setSelectedMode(modeId);
    // Auto-expand when switching modes
    if (!isExpanded) {
      setIsExpanded(true);
    }
  };

  const handleToggleExpand = () => {
    setIsExpanded(!isExpanded);
  };

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'row-reverse',
        backgroundColor: colors.sidebar.background,
        position: 'relative',
      }}
    >
      {/* Mode Switcher Column - Always visible (anchored to the right) */}
      <Box
        sx={{
          width: 56,
          height: '100%',
          backgroundColor: colors.sidebar.background,
          borderLeft: `1px solid ${colors.border.subtle}`,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          py: 1,
          gap: 0.5,
        }}
      >
        {modes.map((mode) => {
          const Icon = mode.icon;
          const isSelected = selectedMode === mode.id;

          return (
            <Tooltip key={mode.id} title={mode.label} placement="left" arrow>
              <IconButton
                onClick={() => handleModeChange(mode.id)}
                sx={{
                  width: 40,
                  height: 40,
                  backgroundColor: isSelected ? colors.sidebar.headerBackground : 'transparent',
                  color: isSelected ? colors.interactive.primary : colors.icon.primary,
                  '&:hover': {
                    backgroundColor: colors.sidebar.headerHover,
                    color: colors.text.primary,
                  },
                  transition: 'all 0.2s ease',
                }}
              >
                <Icon />
              </IconButton>
            </Tooltip>
          );
        })}

        <Box sx={{ flexGrow: 1 }} />

        {/* Expand/Collapse Toggle */}
        <Tooltip title={isExpanded ? 'Collapse' : 'Expand'} placement="left" arrow>
          <IconButton
            onClick={handleToggleExpand}
            sx={{
              width: 32,
              height: 32,
              color: colors.icon.secondary,
              '&:hover': {
                backgroundColor: colors.sidebar.headerHover,
                color: colors.text.primary,
              },
            }}
          >
            {isExpanded ? <ChevronRight /> : <ChevronLeft />}
          </IconButton>
        </Tooltip>

        {/* Close Sidebar Toggle */}
        {onToggle && (
          <Tooltip title="Close Sidebar" placement="left" arrow>
            <IconButton
              onClick={onToggle}
              sx={{
                width: 32,
                height: 32,
                color: colors.icon.secondary,
                '&:hover': {
                  backgroundColor: colors.sidebar.headerHover,
                  color: colors.text.primary,
                },
              }}
            >
              <ChevronRight />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {/* Content Area - Expandable */}
      {isExpanded && (
        <Box
          sx={{
            flex: 1,
            height: '100%',
            overflow: 'hidden',
            minWidth: 280,
            width: '100%',
            backgroundColor: colors.sidebar.background,
            display: 'flex',
            flexDirection: 'column',
            boxSizing: 'border-box',
          }}
        >
          {/* Header matching left sidebar style */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: 3,
              py: 2,
              borderBottom: `1px solid ${colors.border.subtle}`,
            }}
          >
            <Typography
              variant="h6"
              sx={{
                color: colors.text.primary,
                fontSize: '1rem',
                fontWeight: 600,
                letterSpacing: '-0.01em',
                wordBreak: 'break-word',
                overflowWrap: 'break-word',
                flex: 1,
                minWidth: 0, // Allows text to shrink
              }}
            >
              {currentMode?.label || 'Sidebar'}
            </Typography>
            <Button
              onClick={handleToggleExpand}
              size="small"
              endIcon={<ChevronRight sx={{ fontSize: '1.1rem' }} />}
              sx={{
                backgroundColor: 'transparent',
                color: colors.text.secondary,
                borderRadius: '8px',
                textTransform: 'none',
                fontSize: '0.875rem',
                fontWeight: 500,
                px: 1.5,
                py: 0.75,
                minWidth: 'auto',
                '&:hover': {
                  backgroundColor: colors.sidebar.headerHover,
                  color: colors.text.primary,
                },
              }}
            >
              Collapse
            </Button>
          </Box>

          {/* Mode Body Content */}
          {ModeBodyComponent && (
            <Box
              sx={{
                flex: 1,
                overflow: 'auto',
                width: '100%',
                minWidth: 0, // Prevents flex item from overflowing
              }}
            >
              <ModeBodyComponent
                selectedMode={selectedMode}
                isExpanded={isExpanded}
                onSwitchMode={handleModeChange}
                projectId={otherProps.projectId}
                chapters={otherProps.chapters}
                notes={otherProps.notes}
                characters={otherProps.characters}
                families={otherProps.families}
                collaborators={otherProps.collaborators}
                activeProject={otherProps.activeProject}
                formatDate={otherProps.formatDate}
                handleAddChapter={otherProps.handleAddChapter}
                handleAddNote={otherProps.handleAddNote}
                handleEditProject={otherProps.handleEditProject}
                handleAddCharacter={otherProps.handleAddCharacter}
                handleExportProject={otherProps.handleExportProject}
                handleImportDocument={otherProps.handleImportDocument}
                onNoteClick={otherProps.onNoteClick}
                onCharacterClick={otherProps.onCharacterClick}
                onFamilyClick={otherProps.onFamilyClick}
              />
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

RightSidebar.propTypes = {
  modes: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      icon: PropTypes.elementType.isRequired,
      barBodyComponent: PropTypes.elementType.isRequired,
    })
  ),
  defaultMode: PropTypes.string,
  onToggle: PropTypes.func,
};

RightSidebar.defaultProps = {
  modes: [],
  defaultMode: null,
  onToggle: null,
};

export default RightSidebar;
